import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ChangelogSettings } from "@/features/changelog/api/changelog.api";
import { changelogSettingsQueryOptions } from "@/lib/queries";
import { useChangelogPermissions } from "@/features/changelog/hooks/useChangelogPermissions";
import {
  DEFAULT_SPRINT_CONFIG,
  type SprintConfig,
} from "@/features/changelog/lib/changelog-templates";

interface ChangelogSettingsState {
  /** Null while loading, and for anyone who cannot read the settings. */
  settings: ChangelogSettings | null;
  /**
   * True only while a reader is waiting on the fetch. Somebody without the
   * permission is not "loading" — they are never getting an answer, and a
   * caller that waits for one would wait forever.
   */
  isPending: boolean;
}

/**
 * The settings as a page behaviour rather than a form. Used by the deploys tab
 * to decide whether to sync on open and what a sprint is; the settings tab
 * reads the query directly because it also needs the save credit.
 */
export function useChangelogSettings(): ChangelogSettingsState {
  const permissions = useChangelogPermissions();
  const { data, isPending } = useQuery({
    ...changelogSettingsQueryOptions(),
    enabled: permissions.canViewSettings,
  });

  return {
    settings: data ?? null,
    isPending: permissions.canViewSettings && isPending,
  };
}

/** The stored cadence, or the built-in one for a caller who cannot read it. */
export function useSprintConfig(): SprintConfig {
  const { settings } = useChangelogSettings();

  return useMemo(
    () =>
      settings
        ? {
            anchorDate: settings.sprintAnchorDate,
            anchorNumber: settings.sprintAnchorNumber,
            lengthDays: settings.sprintLengthDays,
          }
        : DEFAULT_SPRINT_CONFIG,
    [settings],
  );
}
